/**
 * Port of `repos/opencode/packages/session-ui/src/components/message-part.tsx`, keyed on
 * `styles/components/message-part.css` (vendored byte-identical, 1 569 lines).
 *
 * Upstream dispatches on part type, then on tool name for the tools it presents specially.
 * The same structure is here, over the shape `part-adapter.ts` produces — which is why the
 * tool names below are upstream's (`read`, `grep`, `write`, `apply_patch`) and not ours.
 *
 * Scoped out, per the change proposal: question/answer docks, compaction parts, agent
 * tasks, diagnostics, exa search, and the shell surfaces. None have data behind them here.
 * Their vendored selectors go unmatched, which
 * `test/ui/transcript-selector-coverage.test.tsx` reports rather than fails on.
 */

import { memo, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { ChatAttachment, ChatProposalPart } from "@workerstation/shared";

import { Accordion } from "../opencode/primitives/accordion.tsx";
import { StickyAccordionHeader } from "../opencode/primitives/sticky-accordion-header.tsx";
import { DiffChanges } from "../opencode/primitives/diff-changes.tsx";
import { BasicTool, GenericTool } from "../opencode/basic-tool.tsx";
import { Markdown } from "../opencode/markdown.tsx";
import { ToolErrorCard } from "../opencode/tool-error-card.tsx";
import { Icon } from "../ui/icon.tsx";
import { FileTypeIcon } from "../file-icons.tsx";
import { collapseContext, unifiedDiff } from "../opencode/unified-diff.ts";
import type {
  PatchFileEntry,
  TranscriptPart,
  TranscriptToolPart
} from "../opencode/part-adapter.ts";

export interface MessagePartViewProps {
  isApprovalPending?: (id: string) => boolean;
  isProposalPending?: (id: string) => boolean;
  part: TranscriptPart;
  renderProposalReview?: (proposal: ChatProposalPart) => ReactNode;
  /** Live turn: text is still arriving, so markdown renders in streaming mode. */
  streaming?: boolean;
  /** Start tool bodies expanded. Upstream decides this per tool; tests force it on. */
  defaultOpen?: boolean;
  onApproval?: (approvalId: string, approved: boolean) => void;
  onProposal?: (
    proposalId: string,
    action: "merge" | "discard"
  ) => void;
}

function directoryOf(path: string): string {
  const at = path.lastIndexOf("/");
  return at === -1 ? "" : path.slice(0, at + 1);
}

function filenameOf(path: string): string {
  const at = path.lastIndexOf("/");
  return at === -1 ? path : path.slice(at + 1);
}

/* ------------------------------------------------------------------ text */

function TextPart({ text, streaming }: { text: string; streaming?: boolean }) {
  return (
    <div data-component="text-part">
      <div data-slot="text-part-body">
        <Markdown text={text} live={streaming} />
      </div>
    </div>
  );
}

function ReasoningPart({ text, streaming }: { text: string; streaming?: boolean }) {
  return (
    <div data-component="reasoning-part">
      <Markdown text={text} live={streaming} />
    </div>
  );
}

/* ------------------------------------------------------------------ tools */

interface ToolViewProps {
  part: TranscriptToolPart;
  defaultOpen?: boolean;
}

function ToolOutput({ output }: { output: string }) {
  // `tool-output` deliberately sets no colour: upstream feeds it through `<Markdown>`, and
  // `message-part.css` styles `[data-component="tool-output"] [data-component="markdown"]`.
  // A bare <pre> here inherits from Tailwind instead, which does not follow the skin — it
  // rendered near-black on dark surfaces. The nesting is upstream's, not a workaround.
  return (
    <div data-component="tool-output">
      <div data-component="markdown">
        <pre>{output}</pre>
      </div>
    </div>
  );
}

function ReadTool({ part, defaultOpen }: ToolViewProps) {
  const path = typeof part.state.input.path === "string" ? part.state.input.path : undefined;
  return (
    <BasicTool
      status={part.state.status}
      icon="folder"
      defaultOpen={defaultOpen}
      trigger={{ title: "Read", subtitle: path }}
    >
      {part.state.output ? <ToolOutput output={part.state.output} /> : null}
    </BasicTool>
  );
}

function GrepTool({ part, defaultOpen }: ToolViewProps) {
  const query =
    typeof part.state.input.query === "string"
      ? part.state.input.query
      : typeof part.state.input.pattern === "string"
        ? part.state.input.pattern
        : undefined;
  return (
    <BasicTool
      status={part.state.status}
      icon="magnifying-glass"
      defaultOpen={defaultOpen}
      trigger={{ title: "Search", subtitle: query }}
    >
      {part.state.output ? <ToolOutput output={part.state.output} /> : null}
    </BasicTool>
  );
}

function WriteTool({ part, defaultOpen }: ToolViewProps) {
  const path = typeof part.state.input.path === "string" ? part.state.input.path : undefined;
  return (
    <div data-component="write-tool">
      <BasicTool
        status={part.state.status}
        icon="folder-add-left"
        defaultOpen={defaultOpen}
        trigger={{ title: "Create", subtitle: path }}
      >
        {part.state.output ? <ToolOutput output={part.state.output} /> : null}
      </BasicTool>
    </div>
  );
}

/** One file inside an apply-patch, with its diff behind an accordion. */
function PatchFileEntryView({ file }: { file: PatchFileEntry }) {
  const diff = useMemo(
    () => unifiedDiff(file.before ?? "", file.after ?? ""),
    [file.before, file.after]
  );
  const lines = useMemo(() => collapseContext(diff), [diff]);

  return (
    <Accordion.Item value={file.filePath} data-type={file.type}>
      <StickyAccordionHeader>
        <Accordion.Trigger>
          <div data-slot="apply-patch-trigger-content">
            <div data-slot="apply-patch-file-info">
              <FileTypeIcon path={file.relativePath} />
              <div data-slot="apply-patch-file-name-container">
                {file.relativePath.includes("/") ? (
                  <span data-slot="apply-patch-directory">
                    {`‪${directoryOf(file.relativePath)}‬`}
                  </span>
                ) : null}
                <span data-slot="apply-patch-filename">{filenameOf(file.relativePath)}</span>
              </div>
            </div>
            <div data-slot="apply-patch-trigger-actions">
              {file.type === "add" ? (
                <span data-slot="apply-patch-change" data-type="added">
                  Created
                </span>
              ) : file.type === "delete" ? (
                <span data-slot="apply-patch-change" data-type="removed">
                  Deleted
                </span>
              ) : file.type === "move" ? (
                <span data-slot="apply-patch-change" data-type="modified">
                  Moved
                </span>
              ) : (
                <DiffChanges
                  changes={{ additions: diff.additions, deletions: diff.deletions }}
                />
              )}
              <Icon name="chevron-down" size="small" />
            </div>
          </div>
        </Accordion.Trigger>
      </StickyAccordionHeader>
      <Accordion.Content>
        <div data-component="apply-patch-file-diff">
          <pre>
            {lines.map((line, index) => (
              <div key={index} data-diff-line={line.kind}>
                {line.kind === "added" ? "+" : line.kind === "removed" ? "-" : " "}
                {line.text}
              </div>
            ))}
          </pre>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
}

function ApplyPatchTool({ part, defaultOpen }: ToolViewProps) {
  const files = Array.isArray(part.state.input.files)
    ? (part.state.input.files as PatchFileEntry[])
    : [];
  const [expanded, setExpanded] = useState<string[]>(() =>
    defaultOpen ? files.map((file) => file.filePath) : []
  );

  const subtitle =
    files.length > 0 ? `${files.length} ${files.length === 1 ? "file" : "files"}` : undefined;

  return (
    <div data-component="apply-patch-tool">
      <BasicTool
        status={part.state.status}
        icon="edit"
        defaultOpen={defaultOpen}
        trigger={{ title: "Patch", subtitle }}
      >
        {files.length > 0 ? (
          <Accordion
            multiple
            data-scope="apply-patch"
            value={expanded}
            onValueChange={(value: string | string[]) =>
              setExpanded(Array.isArray(value) ? value : value ? [value] : [])
            }
          >
            {files.map((file) => (
              <PatchFileEntryView key={file.filePath} file={file} />
            ))}
          </Accordion>
        ) : null}
      </BasicTool>
    </div>
  );
}

/**
 * Approve / deny. Ours: opencode has no approval state, and its `tool-action` slot is a
 * 24×24 icon well in the tool header — putting a pair of buttons there clipped them to
 * a quarter of one word. Styled in `shell.css` under our own attribute, inside upstream's
 * tool wrapper so it still sits with the tool it belongs to.
 */
function ApprovalActions({
  approvalId,
  destructive,
  pending = false,
  onApproval
}: {
  approvalId: string;
  destructive?: boolean;
  pending?: boolean;
  onApproval: (approvalId: string, approved: boolean) => void;
}) {
  const submit = (approved: boolean) => { if (!pending) onApproval(approvalId, approved); };

  return (
    <div
      data-component="tool-approval"
      data-destructive={destructive ? "true" : undefined}
      aria-busy={pending || undefined}
    >
      <button
        type="button"
        data-slot="approve"
        disabled={pending}
        onClick={() => void submit(true)}
      >
        Approve
      </button>
      <button
        type="button"
        data-slot="deny"
        disabled={pending}
        onClick={() => void submit(false)}
      >
        Deny
      </button>
    </div>
  );
}

function ToolPart({
  part,
  defaultOpen,
  isApprovalPending,
  onApproval
}: ToolViewProps & {
  isApprovalPending?: (id: string) => boolean;
  onApproval?: (approvalId: string, approved: boolean) => void;
}) {
  let body: ReactNode;

  if (part.state.status === "error" && part.state.error) {
    body = (
      <ToolErrorCard
        title={part.sourceTool}
        subtitle="failed"
        defaultOpen={defaultOpen}
        suffix={
          <pre className="min-w-0 max-w-full whitespace-pre-wrap break-words">
            {part.state.error}
          </pre>
        }
      />
    );
  } else {
    switch (part.tool) {
      case "read":
        body = <ReadTool part={part} defaultOpen={defaultOpen} />;
        break;
      case "grep":
        body = <GrepTool part={part} defaultOpen={defaultOpen} />;
        break;
      case "write":
        body = <WriteTool part={part} defaultOpen={defaultOpen} />;
        break;
      case "apply_patch":
        body = <ApplyPatchTool part={part} defaultOpen={defaultOpen} />;
        break;
      default:
        body = (
          <GenericTool
            tool={part.sourceTool}
            status={part.state.status}
            input={part.state.input}
          />
        );
    }
  }

  return (
    <div data-component="tool-part-wrapper" data-status={part.state.status}>
      {body}
      {part.autoApproved ? (
        <div data-component="tool-auto-approved">
          YOLO auto-approved · no human confirmation
        </div>
      ) : null}
      {part.state.status === "awaiting_approval" && part.approvalId && onApproval ? (
        <ApprovalActions
          approvalId={part.approvalId}
          pending={isApprovalPending?.(part.approvalId) ?? false}
          destructive={part.destructive}
          onApproval={onApproval}
        />
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- proposals */

function ProposalPart({
  part,
  defaultOpen,
  onProposal,
  pending = false,
  renderProposalReview
}: {
  part: Extract<TranscriptPart, { type: "proposal" }>;
  defaultOpen?: boolean;
  pending?: boolean;
  renderProposalReview?: (proposal: ChatProposalPart) => ReactNode;
  onProposal?: (
    proposalId: string,
    action: "merge" | "discard"
  ) => void;
}) {
  const proposal = part.part;
  const state = proposal.pending ? "open" : (proposal.outcome ?? "pending");
  const stateLabel = state.replace("_", " ");
  const submit = (action: "merge" | "discard") => { if (onProposal && !pending) onProposal(proposal.proposalId, action); };

  return (
    <div data-component="tool-part-wrapper" data-status={state}>
      <BasicTool
        defaultOpen={defaultOpen}
        trigger={{
          title: "Proposal",
          subtitle: proposal.summary,
          args: [`${proposal.fileCount} file${proposal.fileCount === 1 ? "" : "s"}`, stateLabel]
        }}
      >
        {renderProposalReview?.(proposal)}
        {proposal.pending && onProposal ? (
          <div data-component="tool-approval" aria-busy={pending || undefined}>
            <button
              type="button"
              data-slot="approve"
              disabled={pending}
              onClick={() => void submit("merge")}
            >
              Merge patch
            </button>
            <button
              type="button"
              data-slot="deny"
              disabled={pending}
              onClick={() => void submit("discard")}
            >
              Discard patch
            </button>
          </div>
        ) : proposal.detail ? (
          <div data-component="tool-output">
            <pre>{proposal.detail}</pre>
          </div>
        ) : null}
      </BasicTool>
    </div>
  );
}

/* ------------------------------------------------------------- dispatcher */

export const MessagePartView = memo(function MessagePartView({
  part,
  streaming,
  defaultOpen,
  onApproval,
  onProposal,
  isApprovalPending,
  isProposalPending,
  renderProposalReview
}: MessagePartViewProps) {
  switch (part.type) {
    case "text":
      return <TextPart text={part.text} streaming={streaming} />;
    case "reasoning":
      return <ReasoningPart text={part.text} streaming={streaming} />;
    case "tool":
      return <ToolPart part={part} defaultOpen={defaultOpen} onApproval={onApproval} isApprovalPending={isApprovalPending} />;
    case "proposal":
      return (
        <ProposalPart part={part} defaultOpen={defaultOpen} onProposal={onProposal} pending={isProposalPending?.(part.part.proposalId) ?? false} renderProposalReview={renderProposalReview} />
      );
  }
});

/** A user's turn, including the attachments carried by the durable user row. */
export function UserMessage({
  text,
  attachments = []
}: {
  text: string;
  attachments?: readonly ChatAttachment[];
}) {
  return (
    <div data-component="user-message">
      {attachments.length > 0 ? (
        <div data-slot="user-message-attachments">
          {attachments.map((attachment) => {
            const image = attachment.mime.startsWith("image/");
            return (
              <div
                key={attachment.id}
                data-slot="user-message-attachment"
                data-type={image ? "image" : "file"}
                title={image ? undefined : attachment.filename}
              >
                {image ? (
                  <img
                    data-slot="user-message-attachment-image"
                    src={attachment.dataUrl}
                    alt={attachment.filename}
                  />
                ) : (
                  <div data-slot="user-message-attachment-file">
                    <FileTypeIcon path={attachment.filename} />
                    <span data-slot="user-message-attachment-name">
                      {attachment.filename}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      <div data-slot="user-message-body">
        <div data-slot="user-message-text">{text}</div>
      </div>
    </div>
  );
}

/** The assistant's side of a turn: every renderable part, in order. */
export function AssistantMessage({ children }: { children?: ReactNode }) {
  return <div data-component="assistant-message">{children}</div>;
}
